/**
 * StatusFilterToggles — 캠페인 상태 필터를 컴팩트한 토글 버튼으로 노출.
 *
 * 드롭다운은 잘 안 눌려서, 상태별 토글 버튼(색상 도트 + 상태명 첫 글자, 툴팁=전체
 * 상태명)으로 대체한다. 중요 상태(draft → in_progress → completed)는 항상 보이고,
 * 나머지는 "⋯" 버튼을 클릭해야 펼쳐진다(한 줄로 컴팩트하게 유지).
 *
 * CampaignSelectDashboard / CampaignMultiSelect 등에서 공유한다.
 */
import { useState } from 'react';
import Tooltip from '@/components/Tooltip';
import { useI18nText } from '@/lib/utils/i18nText';
import { CAMPAIGN_STATUS_ICON, CAMPAIGN_STATUS_LABEL } from '@/components/CampaignSelect';
import { CAMPAIGN_STATUS_DOT_CLASS } from '@/lib/utils/campaignStatusStyle';

/** Always-visible statuses, in this order; the rest reveal on clicking "⋯". */
const STATUS_TOGGLE_PRIMARY = ['draft', 'in_progress', 'completed'];

export interface StatusFilterTogglesProps {
  value: string | null;
  onChange: (v: string | null) => void;
  statusCounts: Record<string, number>;
  totalCount: number;
  /** Statuses to hide from the toggle row. */
  excludeStatuses?: string[];
  /**
   * When false, all statuses are shown at once (no primary/secondary split and
   * no "+"/"-" collapse button). Default true. Useful where the status set is
   * small (e.g. the campaign-history popover).
   */
  collapsible?: boolean;
}

export function StatusFilterToggles({
  value, onChange, statusCounts, totalCount, excludeStatuses, collapsible = true,
}: StatusFilterTogglesProps) {
  const { t } = useI18nText();
  const [expanded, setExpanded] = useState(false);
  const available = Object.keys(CAMPAIGN_STATUS_ICON).filter(
    (s) => !excludeStatuses?.includes(s) && (statusCounts[s] ?? 0) > 0,
  );
  const primary = STATUS_TOGGLE_PRIMARY.filter((s) => available.includes(s));
  const secondary = available.filter((s) => !STATUS_TOGGLE_PRIMARY.includes(s));

  const renderToggle = (status: string) => {
    const count = statusCounts[status] ?? 0;
    const label = CAMPAIGN_STATUS_LABEL[status] ?? status.replace(/_/g, ' ');
    const dotClass = CAMPAIGN_STATUS_DOT_CLASS[status] ?? 'bg-ggc-border-gray';
    const letter = label.charAt(0).toUpperCase();
    const isActive = value === status;
    return (
      <Tooltip key={status} content={`${label} (${count})`}>
        <button
          type="button"
          className={`campaign-status-toggle${isActive ? ' campaign-status-toggle--active' : ''}`}
          onClick={() => onChange(isActive ? null : status)}
          aria-label={label}
          aria-pressed={isActive}
        >
          <span className={`inline-block size-[6px] rounded-full shrink-0 ${dotClass}`} />
          <span className="campaign-status-toggle__letter">{letter}</span>
        </button>
      </Tooltip>
    );
  };

  return (
    <div className="campaign-status-toggles">
      <button
        type="button"
        className={`campaign-status-toggle campaign-status-toggle--all${value === null ? ' campaign-status-toggle--active' : ''}`}
        onClick={() => onChange(null)}
      >
        {t('All', '전체')}
        <span className="campaign-status-toggle__count">{totalCount}</span>
      </button>
      {primary.map((s) => renderToggle(s))}
      {/* Secondary statuses: all shown when not collapsible; otherwise revealed
          via the "+"/"-" button (or if currently the active filter). */}
      {(collapsible ? secondary.filter((s) => expanded || value === s) : secondary).map((s) => renderToggle(s))}
      {collapsible && secondary.length > 0 && (
        <Tooltip content={expanded ? t('Collapse', '접기') : t('More statuses', '기타 상태')}>
          <button
            type="button"
            className="campaign-status-toggle campaign-status-toggle--more"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? t('Collapse', '접기') : t('More statuses', '기타 상태')}
          >
            <span className="campaign-status-toggle__letter">{expanded ? '-' : '+'}</span>
          </button>
        </Tooltip>
      )}
    </div>
  );
}
